import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import {
  ArrowRight,
  Loader2,

} from 'lucide-react';
import { DEMO_TRANSACTIONS } from '@/data/demo-data';
import { formatDate } from '@/lib/utils';
import { useFormatting } from '@/hooks/useFormatting';
import { TransactionDetailModal } from '@/components/modals/TransactionDetailModal';
import { usePermissions } from '@/hooks/usePermissions';
import { useEntityFilter } from '@/hooks/useEntityFilter';
import { useApp } from '@/contexts/AppContext';
import { getRecentTransactions } from '@/api/services/recentTransactionService';
import type { RecentTransactionApi } from '@/api/models/recentTransaction';
import type { Transaction } from '@/types';

// ─── Colour constants ────────────────────────────────────────────────────────
const TEAL = 'oklch(0.5 0.0896 194.8)';
const SUCCESS = 'var(--mc-success-icon)';
const ERROR_COL = 'var(--mc-error-icon)';
const NEUTRAL_TXT = '#6b7280';
const CONTENT_TXT = 'oklch(0.2764 0.021 248.59)';

// ─── Types ───────────────────────────────────────────────────────────────────
interface TransactionDisplay {
  id: number;
  date: Date;
  description: string;
  category: string;
  status: string;
  grossValueIn: number;
  grossValueOut: number;
  reference?: string;
  dataSourceName?: string;
}

type SortDir = 'asc' | 'desc' | null;

interface ColDef {
  key: keyof TransactionDisplay;
  label: string;
  minWidth: number;
  align?: 'left' | 'right';
}

// ─── ResizeHandle ─────────────────────────────────────────────────────────────
function ResizeHandle({ onResize }: { onResize: (delta: number) => void }) {
  const [dragging, setDragging] = useState(false);
  const [hovering, setHovering] = useState(false);
  const startX = useRef(0);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    startX.current = e.clientX;
    const doc = e.currentTarget.ownerDocument;
    const onMove = (ev: MouseEvent) => {
      onResize(ev.clientX - startX.current);
      startX.current = ev.clientX;
    };
    const onUp = () => {
      setDragging(false);
      doc.removeEventListener('mousemove', onMove);
      doc.removeEventListener('mouseup', onUp);
    };
    doc.addEventListener('mousemove', onMove);
    doc.addEventListener('mouseup', onUp);
  };

  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        position: 'absolute',
        right: -4,
        top: 0,
        height: '100%',
        width: 8,
        zIndex: 10,
        cursor: 'col-resize',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: 2,
          height: '60%',
          backgroundColor: hovering || dragging ? TEAL : 'transparent',
          borderRadius: 1,
          transition: 'background-color 0.15s',
        }}
      />
    </div>
  );
}

// ─── SortHeader ───────────────────────────────────────────────────────────────
function SortHeader({
  col,
  sortKey,
  sortDir,
  width,
  onSort,
  onResize,
}: {
  col: ColDef;
  sortKey: keyof TransactionDisplay | null;
  sortDir: SortDir;
  width: number;
  onSort: (key: keyof TransactionDisplay) => void;
  onResize: (delta: number) => void;
}) {
  const active = sortKey === col.key && sortDir != null;
  const [hoverLabel, setHoverLabel] = useState(false);
  return (
    <th
      style={{
        width,
        minWidth: col.minWidth,
        position: 'relative',
        overflow: 'visible',
        padding: '10px 12px',
        textAlign: col.align ?? 'left',
        fontSize: 14,
        fontWeight: 600,
        color: 'oklch(0.2764 0.021 248.59)',
        backgroundColor: '#ffffff',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <button
        type="button"
        onClick={() => onSort(col.key)}
        onMouseEnter={() => setHoverLabel(true)}
        onMouseLeave={() => setHoverLabel(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start',
          width: '100%',
          gap: 4,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          color: active || hoverLabel ? TEAL : 'oklch(0.2764 0.021 248.59)',
          fontWeight: 600,
          fontSize: 14,
          transition: 'color 0.15s',
        }}
      >
        {col.label}
        {active && (
          <span style={{ fontSize: 11, lineHeight: 1 }}>
            {sortDir === 'desc' ? '↓' : '↑'}
          </span>
        )}
      </button>
      <ResizeHandle onResize={onResize} />
    </th>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
interface RecentTransactionsCardProps {
  onNavigate?: (page: string) => void;
}

const COLS: ColDef[] = [
  { key: 'date',          label: 'Date',        minWidth: 100 },
  { key: 'description',   label: 'Description', minWidth: 160 },
  { key: 'category',      label: 'Category',    minWidth: 120 },
  { key: 'status',        label: 'Status',      minWidth: 100 },
  { key: 'grossValueIn',  label: 'In',          minWidth: 100, align: 'right' as const },
  { key: 'grossValueOut', label: 'Out',         minWidth: 100, align: 'right' as const },
];

const DEFAULT_WIDTHS: Record<string, number> = {
  date: 110,
  description: 200,
  category: 130,
  status: 110,
  grossValueIn: 110,
  grossValueOut: 110,
};

export default function RecentTransactionsCard({ onNavigate }: RecentTransactionsCardProps) {
  const { hasPermission } = usePermissions();
  const { filterByEntity } = useEntityFilter();
  const { mode } = useApp();
  const { formatCurrency } = useFormatting();

  const [liveTransactions, setLiveTransactions] = useState<TransactionDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [colWidths, setColWidths] = useState<Record<string, number>>(DEFAULT_WIDTHS);
  const [sortKey, setSortKey] = useState<keyof TransactionDisplay | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  // ── Live mode fetch ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (mode === 'live') {
      setIsLoading(true);
      setError(null);
      getRecentTransactions()
        .then((response) => {
          const mapped = response.transactions.map((t: RecentTransactionApi) => ({
            id: t.id,
            date: new Date(t.transactionDate),
            description: t.description || '',
            category: t.category || 'Uncategorised',
            status: t.status,
            grossValueIn: t.grossValueIn,
            grossValueOut: t.grossValueOut,
          }));
          setLiveTransactions(mapped.slice(0, 10));
        })
        .catch((err) => {
          console.error('Failed to fetch recent transactions:', err);
          setError('Failed to load transactions');
        })
        .finally(() => setIsLoading(false));
    }
  }, [mode]);

  if (!hasPermission('view_recent_transactions_card')) return null;

  // ── Raw data — top 10 most recent ────────────────────────────────────────────
  const rawTransactions: TransactionDisplay[] =
    mode === 'live'
      ? liveTransactions
      : filterByEntity(DEMO_TRANSACTIONS)
          .slice(0, 10)
          .map((t) => ({
            id: t.id as unknown as number,
            date: t.date,
            description: t.description,
            category: t.category,
            status: t.status,
            grossValueIn: t.amount > 0 ? t.amount : 0,
            grossValueOut: t.amount < 0 ? Math.abs(t.amount) : 0,
            reference: t.reference,
            dataSourceName: t.dataSourceName,
          }));

  // ── Sort ─────────────────────────────────────────────────────────────────────
  const displayRows = sortKey && sortDir
    ? [...rawTransactions].sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        let cmp = 0;
        if (av instanceof Date && bv instanceof Date) cmp = av.getTime() - bv.getTime();
        else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
        else cmp = String(av ?? '').localeCompare(String(bv ?? ''));
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : rawTransactions;

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleSort = (key: keyof TransactionDisplay) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('desc'); }
    else if (sortDir === 'desc') setSortDir('asc');
    else { setSortKey(null); setSortDir(null); }
  };

  const handleResize = (key: keyof TransactionDisplay, delta: number) => {
    setColWidths((prev) => ({
      ...prev,
      [key]: Math.max(40, (prev[key] ?? DEFAULT_WIDTHS[key] ?? 100) + delta),
    }));
  };

  const handleRowClick = (txn: TransactionDisplay) => {
    const fullTxn = DEMO_TRANSACTIONS.find((t) => t.id === (txn.id as unknown as string));
    if (fullTxn) {
      setSelectedTransaction(fullTxn);
      setShowTransactionModal(true);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getStatusBadge = (status: string): { variant: 'success' | 'warning' | 'destructive' | 'info' | 'secondary', label: string } => {
    switch (status) {
      case 'Settled':      return { variant: 'success',     label: 'Settled' };
      case 'Outstanding':  return { variant: 'warning',     label: 'Outstanding' };
      case 'Overdue':      return { variant: 'destructive', label: 'Overdue' };
      case 'Unknown':      return { variant: 'secondary',   label: 'Unknown' };
      default:             return { variant: 'info',        label: status };
    }
  };

  const getCategoryStyle = (category: string) => {
    if (category === 'Uncategorised' || category === 'Uncategorized')
      return { bg: 'var(--mc-error-bg)', text: 'var(--mc-error-icon)' };
    return { bg: 'var(--mc-neutral-bg)', text: 'var(--mc-neutral-icon)' };
  };

  const totalTableWidth = COLS.reduce((sum, c) => sum + (colWidths[c.key] ?? c.minWidth), 0);

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Card>
        {/* ── Card header ─────────────────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>
              Recent Transactions
            </span>
            <InfoTooltip
              title="Recent Transactions"
              content="The 10 most recent transactions across your connected accounts. Click any row to view full details."
            />
          </div>
        </div>

        {/* ── Table ────────────────────────────────────────────────────────────── */}
        {isLoading && mode === 'live' ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error && mode === 'live' ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32, color: ERROR_COL }}>
            {error}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                minWidth: Math.max(600, totalTableWidth),
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {COLS.map((col) => (
                    <SortHeader
                      key={col.key}
                      col={col}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      width={colWidths[col.key] ?? col.minWidth}
                      onSort={handleSort}
                      onResize={(delta) => handleResize(col.key, delta)}
                    />
                  ))}
                </tr>
              </thead>

              <tbody>
                {displayRows.map((txn, idx) => {
                  const isHovered = hoveredRow === idx;
                  const rowBg = isHovered ? '#e5e7eb' : idx % 2 === 0 ? '#ffffff' : '#f9fafb';
                  const statusBadge = getStatusBadge(txn.status);
                  const catStyle = getCategoryStyle(txn.category);

                  return (
                    <tr
                      key={txn.id}
                      onMouseEnter={() => setHoveredRow(idx)}
                      onMouseLeave={() => setHoveredRow(null)}
                      onClick={() => handleRowClick(txn)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Date */}
                      <td
                        style={{
                          width: colWidths.date ?? DEFAULT_WIDTHS.date,
                          padding: '10px 12px',
                          fontSize: 14,
                          color: CONTENT_TXT,
                          backgroundColor: rowBg,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {formatDate(txn.date)}
                      </td>

                      {/* Description */}
                      <DescriptionCell
                        value={txn.description}
                        isRowHovered={isHovered}
                        rowBg={rowBg}
                        width={colWidths.description ?? DEFAULT_WIDTHS.description}
                      />

                      {/* Category */}
                      <td
                        style={{
                          width: colWidths.category ?? DEFAULT_WIDTHS.category,
                          padding: '10px 12px',
                          fontSize: 14,
                          color: CONTENT_TXT,
                          backgroundColor: rowBg,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {txn.category}
                      </td>

                      {/* Status */}
                      <td
                        style={{
                          width: colWidths.status ?? DEFAULT_WIDTHS.status,
                          padding: '10px 12px',
                          fontSize: 13,
                          backgroundColor: rowBg,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                      </td>

                      {/* In */}
                      <td
                        style={{
                          width: colWidths.grossValueIn ?? DEFAULT_WIDTHS.grossValueIn,
                          padding: '10px 12px',
                          fontSize: 13,
                          textAlign: 'right',
                          backgroundColor: rowBg,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {txn.grossValueIn > 0 ? (
                          <span style={{ color: SUCCESS, fontWeight: 600 }}>
                            +{formatCurrency(txn.grossValueIn, 'full')}
                          </span>
                        ) : (
                          <span style={{ color: NEUTRAL_TXT }}>—</span>
                        )}
                      </td>

                      {/* Out */}
                      <td
                        style={{
                          width: colWidths.grossValueOut ?? DEFAULT_WIDTHS.grossValueOut,
                          padding: '10px 12px',
                          fontSize: 13,
                          textAlign: 'right',
                          backgroundColor: rowBg,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {txn.grossValueOut > 0 ? (
                          <span style={{ color: ERROR_COL, fontWeight: 600 }}>
                            -{formatCurrency(txn.grossValueOut, 'full')}
                          </span>
                        ) : (
                          <span style={{ color: NEUTRAL_TXT }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Footer nav ───────────────────────────────────────────────────────── */}
        <div
          style={{
            padding: '8px 16px',
            borderTop: '1px solid #f3f4f6',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={() => onNavigate?.('transactions')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              color: '#111827',
              padding: '6px 12px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              background: 'none',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'oklch(0.96 0.02 194.8)';
              (e.currentTarget as HTMLButtonElement).style.color = TEAL;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.color = '#111827';
            }}
          >
            View all transactions
            <ArrowRight size={14} />
          </button>
        </div>
      </Card>

      {/* ── Transaction detail modal ──────────────────────────────────────────── */}
      {showTransactionModal && selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          open={showTransactionModal}
          onClose={() => {
            setShowTransactionModal(false);
            setSelectedTransaction(null);
          }}
        />
      )}
    </>
  );
}

// ─── DescriptionCell ─────────────────────────────────────────────────────────
function DescriptionCell({
  value,
  isRowHovered,
  rowBg,
  width,
}: {
  value: string;
  isRowHovered: boolean;
  rowBg: string;
  width: number;
}) {
  const [hover, setHover] = useState(false);
  return (
    <td
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width,
        padding: '10px 12px',
        fontSize: 14,
        backgroundColor: rowBg,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: TEAL,
        textDecoration: hover || isRowHovered ? 'underline' : 'none',
        transition: 'text-decoration 0.1s',
      }}
    >
      {value}
    </td>
  );
}
